/**
 * MUI 5 theme — the single source of truth for the dashboard's look after
 * the migration off Tailwind/Radix.
 *
 * The values here are the EXACT sRGB-hex conversions of the OKLCH design
 * tokens that used to live in `globals.css` (Linear/Vercel/Stripe — quiet,
 * monochrome, surgical). MUI 5's colour utilities (`alpha`, `darken`,
 * contrast-text computation) can't parse `oklch()`, so we bake the tokens
 * to hex once here and theme MUI to reproduce the prior look on top of
 * Material mechanics (elevation, ripple, motion).
 *
 *   - Brand navy is the ONE chromatic accent (primary). Lime is secondary.
 *   - Surfaces are near-neutral; borders are hairline `divider`.
 *   - Card shadows are the quiet 2-layer set, NOT bloomy Material elevation.
 *   - Body = Inter, headings = DM Sans, buttons are sentence-case.
 *
 * Hex values are produced by scripts/oklch-to-hex (see docs/mui-migration.md);
 * change a token in that table + rerun to re-skin.
 */
import { createTheme, type Theme, type ThemeOptions } from "@mui/material/styles";

/* ── Per-system accent colours (the NeedsAttention queue cards). Exposed as
 * `theme.palette.system.*` via the augmentation below. ───────────────── */
type SystemAccents = {
  support: string;
  crm: string;
  sales: string;
  docs: string;
  marketing: string;
};

/* ── Chat surface tokens (the `/chat` WhatsApp-style messenger). The Chat
 * page is the ONE place we deliberately step off the navy/lime brand and
 * adopt WhatsApp's signature look (per an explicit product request): a beige
 * wallpaper, green outgoing bubbles, white incoming bubbles, and blue read
 * ticks. Centralised here (not raw hex in components) so the convention —
 * "colour comes from the theme" — still holds, and a dark-mode variant
 * comes for free. Exposed as `theme.palette.chat.*`. See docs/chat.md. */
type ChatTokens = {
  wallpaper: string;
  bubbleOut: string;
  bubbleOutText: string;
  bubbleIn: string;
  bubbleInText: string;
  headerBg: string;
  accent: string;
  tick: string;
  tickRead: string;
  meta: string;
};

/* ── Presence-dot colours. `online` must read as a VIVID, bright green and
 * `offline` as a muted light grey so the two are unmistakable at a glance under
 * any monitor / ambient lighting (explicit product request — the old muted
 * green vs grey was too close). Themed (not raw hex in components) + dark-mode
 * aware. Exposed as `theme.palette.presence.*`; used by `PresenceDot`. */
type PresenceTokens = {
  online: string;
  offline: string;
};

declare module "@mui/material/styles" {
  interface Palette {
    system: SystemAccents;
    chat: ChatTokens;
    presence: PresenceTokens;
    /** Recessed surface for sub-boxes inside a card (was --color-bg-soft). */
    surfaceSoft: string;
  }
  interface PaletteOptions {
    system?: SystemAccents;
    chat?: ChatTokens;
    presence?: PresenceTokens;
    surfaceSoft?: string;
  }
}

/* ── Baked tokens (exact OKLCH→sRGB). ─────────────────────────────────── */
const light = {
  bg: "#f7fafd",
  bgElev: "#ffffff",
  bgSoft: "#eff4f9",
  fg: "#0a1018",
  fgSoft: "#3d434a",
  fgMute: "#70757c",
  line: "#e9ebee",
  brand: "#2a3e6a",
  brandFrom: "#3e5993",
  brandLime: "#acce3e",
  good: "#1b9946",
  bad: "#d72630",
  warn: "#dc8900",
  system: {
    support: "#b9b9e4",
    crm: "#97d7c5",
    sales: "#f0c595",
    docs: "#ceb9e9",
    marketing: "#ecb5ce",
  },
  chat: {
    wallpaper: "#efeae2",
    bubbleOut: "#d9fdd3",
    bubbleOutText: "#111b21",
    bubbleIn: "#ffffff",
    bubbleInText: "#111b21",
    headerBg: "#f0f2f5",
    accent: "#00a884",
    tick: "#8696a0",
    tickRead: "#53bdeb",
    meta: "#667781",
  },
  presence: {
    online: "#00c853", // vivid, bright green — pops on light backgrounds
    offline: "#c2cad2", // light, muted grey — clearly "off"
  },
} as const;

const dark = {
  bg: "#070708",
  bgElev: "#0e1011",
  bgSoft: "#141618",
  fg: "#f5f5f5",
  fgSoft: "#b5b8ba",
  fgMute: "#787b7d",
  line: "#191b1c",
  brand: "#799ee3",
  brandFrom: "#94b8f8",
  brandFg: "#070b14",
  brandLime: "#b4d24a",
  good: "#46b964",
  bad: "#f84b4b",
  warn: "#f7a224",
  system: {
    support: "#b8b8ea",
    crm: "#8bd6c1",
    sales: "#f4c48d",
    docs: "#d0b8ee",
    marketing: "#f0b2cf",
  },
  chat: {
    wallpaper: "#0b141a",
    bubbleOut: "#005c4b",
    bubbleOutText: "#e9edef",
    bubbleIn: "#202c33",
    bubbleInText: "#e9edef",
    headerBg: "#202c33",
    accent: "#00a884",
    tick: "#8696a0",
    tickRead: "#53bdeb",
    meta: "#8696a0",
  },
  presence: {
    online: "#27f07e", // vivid, bright green — pops on near-black
    offline: "#586771", // dim grey — clearly "off" against the dark surface
  },
} as const;

/* ── Quiet 2-layer card shadows (was --shadow-card / -hover). ──────────── */
const shadowCardLight = "0 1px 2px rgba(0,0,0,0.06), 0 10px 24px -8px rgba(0,0,0,0.12)";
const shadowHoverLight = "0 2px 4px rgba(0,0,0,0.07), 0 18px 36px -12px rgba(0,0,0,0.18)";
const shadowCardDark = "0 1px 2px rgba(0,0,0,0.5), 0 12px 28px -10px rgba(0,0,0,0.5)";
const shadowHoverDark = "0 2px 4px rgba(0,0,0,0.55), 0 20px 40px -12px rgba(0,0,0,0.65)";

/**
 * MUI requires a 25-entry `shadows` array (elevations 0–24). We keep the
 * whole product inside the quiet 2-layer family: 0 = none, 1–2 = the card
 * shadow, 3–24 = the slightly-raised hover shadow (menus, dialogs, popovers).
 * Nothing gets the default bloomy Material drop.
 */
function buildShadows(mode: "light" | "dark"): Theme["shadows"] {
  const card = mode === "light" ? shadowCardLight : shadowCardDark;
  const hover = mode === "light" ? shadowHoverLight : shadowHoverDark;
  const arr: string[] = ["none", card, card];
  for (let i = 3; i < 25; i += 1) arr.push(hover);
  return arr as unknown as Theme["shadows"];
}

const fontSans =
  '"Inter", "Inter var", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const fontDisplay = '"DM Sans", "Inter", "Inter var", ui-sans-serif, system-ui, sans-serif';

/* ── Corner radius — THE single global knob. ──────────────────────────
 * `shape.borderRadius` (below) is the ONE source of truth for rounded
 * corners across the whole app. MUI is designed around it: every
 * component (Card, Button, TextField, Menu, Dialog, Chip, Alert, …)
 * derives its radius from `shape.borderRadius`, and the `sx`
 * `borderRadius: n` prop multiplies it (`borderRadius: 2` ⇒ 2 × RADIUS,
 * `borderRadius: 0.5` ⇒ 4px at RADIUS 8). So changing this single
 * number re-rounds the entire system — do NOT hard-code a px radius on
 * a component override or it will stop following the knob. */
const RADIUS = 8;

function getThemeOptions(mode: "light" | "dark"): ThemeOptions {
  const t = mode === "light" ? light : dark;
  const isLight = mode === "light";
  const brandContrast = isLight ? light.bgElev : dark.brandFg;

  return {
    palette: {
      mode,
      primary: {
        main: t.brand,
        light: t.brandFrom,
        contrastText: brandContrast,
      },
      secondary: {
        main: t.brandLime,
        contrastText: isLight ? light.fg : "#10210a",
      },
      success: { main: t.good, contrastText: "#ffffff" },
      error: { main: t.bad, contrastText: "#ffffff" },
      warning: { main: t.warn, contrastText: isLight ? "#3a2400" : "#1f1300" },
      info: { main: t.brandFrom, contrastText: "#ffffff" },
      background: { default: t.bg, paper: t.bgElev },
      text: { primary: t.fg, secondary: t.fgSoft, disabled: t.fgMute },
      divider: t.line,
      system: t.system,
      chat: t.chat,
      presence: t.presence,
      surfaceSoft: t.bgSoft,
      action: {
        hover: t.bgSoft,
        // brand-tinted soft pill for active nav / selected rows (was
        // --color-brand-soft). MUI applies `selected` at low opacity, so use
        // the brand hue and let MUI tint it.
        selected: isLight ? "rgba(42,62,106,0.10)" : "rgba(121,158,227,0.16)",
      },
    },
    shape: { borderRadius: RADIUS },
    shadows: buildShadows(mode),
    typography: {
      fontFamily: fontSans,
      fontSize: 14,
      // THE base text-size knob for the whole app. MUI's default `body1`
      // is 1rem (16px) — and `body1` is what every unstyled surface falls
      // back to: `<Typography>`, `InputBase`/`TextField`/`Select` inputs,
      // `FormLabel`, `MenuItem`, `ListItemText`, dialog text, … The rest of
      // the dashboard, though, renders at 14px (there are ~800 `fontSize:
      // 14` sx hardcodes), so any component that DIDN'T hardcode leaked
      // through at 16px — that's the "CRM (and the odd control) looks
      // bigger than everything else" mix. We make 14px the theme default so
      // the base size lives in ONE place and nothing has to hardcode it:
      // change this line (+ the CssBaseline `body` size below) to re-scale
      // the app's body text. Explicit per-element sizes (12px captions, 18px
      // sub-heads, the intentional 16px emphases) still win. Headings (h1–h6)
      // keep their own scale.
      body1: { fontSize: "0.875rem" },
      // Headings render in the display face (DM Sans), tighter tracking — the
      // base-layer h1–h6 rule from globals.css, expressed per-variant.
      h1: { fontFamily: fontDisplay, fontWeight: 600, letterSpacing: "-0.011em" },
      h2: { fontFamily: fontDisplay, fontWeight: 600, letterSpacing: "-0.011em" },
      h3: { fontFamily: fontDisplay, fontWeight: 600, letterSpacing: "-0.011em" },
      h4: { fontFamily: fontDisplay, fontWeight: 600, letterSpacing: "-0.011em" },
      h5: { fontFamily: fontDisplay, fontWeight: 600, letterSpacing: "-0.011em" },
      h6: { fontFamily: fontDisplay, fontWeight: 600, letterSpacing: "-0.011em" },
      button: { textTransform: "none", fontWeight: 500 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "html, body, #root": { height: "100%" },
          body: {
            // Base size for plain-HTML text that inherits from <body>
            // (matches `typography.body1` above so MUI components and raw
            // DOM text render at the SAME 14px — no 14-vs-16 mix). rem stays
            // relative to <html> (16px), so MUI's pxToRem scale is unaffected.
            fontSize: "0.875rem",
            letterSpacing: "-0.005em",
            fontFeatureSettings: '"tnum", "cv11"',
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
        },
      },
      // Flat by default — no Material gradient overlay on raised surfaces.
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: "none" } },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          // No `borderRadius` here — Card inherits `shape.borderRadius`
          // (the single radius knob) the MUI way.
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
          }),
        },
      },
      // MuiButton needs no override: radius inherits `shape.borderRadius`
      // (the single knob), sentence-case comes from `typography.button`,
      // and the Material ripple stays on by default.
      MuiTextField: { defaultProps: { size: "small", variant: "outlined" } },
      MuiSelect: { defaultProps: { size: "small" } },
      MuiFormControl: { defaultProps: { size: "small" } },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: ({ theme }) => ({ borderColor: theme.palette.divider }),
        },
      },
      MuiAppBar: {
        defaultProps: { color: "inherit", elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundImage: "none",
          }),
        },
      },
      // ── Tabs = ONE segmented control, app-wide. ───────────────────────
      // Every MUI `<Tabs>` renders as a soft recessed track (surfaceSoft +
      // hairline) with the SELECTED tab lifted out as a paper pill that
      // slides between tabs — MUI's own indicator, restyled from a 2px
      // underline into the full-height pill, so the slide animation comes
      // for free and no call site has to hide it. The hand-rolled strips
      // (`crm/components/detail-tabs.tsx`, the `<SectionHeader>` `TabLink`)
      // reproduce this exact look so a tab reads the same on every page.
      // Radii derive from the single `shape.borderRadius` knob: the track
      // is knob+2, the pill knob-2 (concentric with the 4px track padding).
      // Pinned by `theme.test.ts` → "tabs". See docs/ui-conventions.md → "Tabs".
      MuiTabs: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: 40,
            padding: 4,
            borderRadius: theme.shape.borderRadius + 2,
            backgroundColor: theme.palette.surfaceSoft,
            border: `1px solid ${theme.palette.divider}`,
          }),
          indicator: ({ theme }) => ({
            top: 0,
            bottom: 0,
            height: "100%",
            borderRadius: Math.max(theme.shape.borderRadius - 2, 4),
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
            boxSizing: "border-box",
            zIndex: 0,
          }),
          flexContainer: { gap: 2, position: "relative", zIndex: 1 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: "none",
            fontWeight: 500,
            minHeight: 32,
            padding: "6px 14px",
            borderRadius: Math.max(theme.shape.borderRadius - 2, 4),
            color: theme.palette.text.secondary,
            zIndex: 1,
            transition: theme.transitions.create(["color", "background-color"], {
              duration: 150,
            }),
            "&:hover": { color: theme.palette.text.primary },
            "&.Mui-selected": { color: theme.palette.primary.main, fontWeight: 600 },
            "&.Mui-focusVisible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: -2,
            },
          }),
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            backgroundColor: theme.palette.text.primary,
            color: theme.palette.background.paper,
            fontSize: "0.75rem",
          }),
        },
      },
      MuiLink: { defaultProps: { underline: "hover" } },
      // Compact, consistent table rhythm system-wide. The real row-height
      // floor in our tables is NOT the cell padding — it's the *controls*:
      // a default MUI <Checkbox> is ~42px and the row-action <IconButton>
      // is pinned to 32px, so a row can never shrink below its tallest
      // control no matter how tight the padding. (An earlier pass trimmed
      // padding only and the checkbox/action tables stayed ~62px — the
      // "rows STILL way too tall" report.) So we cut padding to 4px AND
      // tame the selection checkbox + action icon-button *inside table
      // cells only* — forms/toolbars keep full-size, tappable controls.
      // Net: ~32px single-line rows vs the old ~62px.
      //
      // The control rules live on `root` so they apply to BOTH default and
      // `size="small"` cells. The descendant selector
      // `.MuiTableCell-root .MuiIconButton-root` (specificity 0,2,0)
      // intentionally outranks the per-button inline `sx={{ width: 32,
      // height: 32 }}` (one class, 0,1,0) — so the fix needs ZERO page
      // edits and can't be silently re-broken by the next copy-pasted 32px
      // row-action button. A cell that hand-sets its own `py` via `sx`
      // still wins for padding (equal specificity → sx, injected last).
      // Row height stays content-driven — we never pin a fixed row height,
      // so the CRM's multi-line cells aren't clipped. The CRM shares this
      // one theme (single ThemeProvider in main.tsx), so all 13 tables get
      // it. Pinned by theme.test.ts + crm_contacts_density.cy.ts.
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(0.5, 2), // 4px / 16px
            // Selection checkbox — no inline size on it, so a clean tame:
            // 20px glyph + 2px padding ⇒ ~24px box (was ~42px).
            "& .MuiCheckbox-root": {
              padding: theme.spacing(0.25),
              "& .MuiSvgIcon-root": { fontSize: "1.25rem" },
            },
            // Row-action icon button — shed the inline 32px box (see the
            // specificity note above) so it sizes to its 16px glyph ⇒
            // ~24px. width/height:auto restore MUI's padding-driven sizing.
            "& .MuiIconButton-root": {
              width: "auto",
              height: "auto",
              padding: theme.spacing(0.5),
            },
          }),
          sizeSmall: ({ theme }) => ({ padding: theme.spacing(0.5, 1.5) }),
        },
      },
    },
  };
}

export function getTheme(mode: "light" | "dark"): Theme {
  return createTheme(getThemeOptions(mode));
}

/** Light is the runtime default (matches today; nothing toggles dark yet). */
export const theme = getTheme("light");
export const darkTheme = getTheme("dark");
